/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

export interface NavTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  shortLabel?: string;
}

interface TabNavigationState {
  tabs: NavTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  visible: boolean;
}

interface TabNavigationContextType extends TabNavigationState {
  setNav: (state: TabNavigationState) => void;
  /** Primary action for the active page, surfaced in the mobile top bar. */
  headerAction: React.ReactNode;
  setHeaderAction: (node: React.ReactNode) => void;
}

const defaultState: TabNavigationState = {
  tabs: [],
  activeTab: "",
  onTabChange: () => {},
  visible: false,
};

const TabNavigationContext = createContext<TabNavigationContextType | undefined>(
  undefined
);

// Lets a route-level component (Main) own the tabs/activeTab/onTabChange state
// while a parent layout component (App) renders the actual <TabNavigation> in
// its header -- App can't reach into Main's local state directly since Main
// is rendered underneath it via <Outlet/>.
export function TabNavigationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TabNavigationState>(defaultState);
  const [headerAction, setHeaderAction] = useState<React.ReactNode>(null);

  const value = useMemo(
    () => ({ ...state, setNav: setState, headerAction, setHeaderAction }),
    [state, headerAction]
  );

  return (
    <TabNavigationContext.Provider value={value}>
      {children}
    </TabNavigationContext.Provider>
  );
}

export function useTabNavigationContext() {
  const ctx = useContext(TabNavigationContext);
  if (!ctx) {
    throw new Error(
      "useTabNavigationContext must be used within a TabNavigationProvider"
    );
  }
  return ctx;
}

/**
 * Publish a page's primary action into the mobile top bar for the lifetime of
 * the component. `render` returns the control (keep it a compact icon button --
 * the bar is short); `deps` control when it is rebuilt, same contract as a
 * useEffect dependency list. Cleared automatically on unmount.
 */
export function useMobileHeaderAction(
  render: () => React.ReactNode,
  deps: React.DependencyList
): void {
  const { setHeaderAction } = useTabNavigationContext();
  useEffect(() => {
    setHeaderAction(render());
    return () => setHeaderAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
